import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate } from 'react-router-dom'
import { improveApi } from '../../api/improve'
import { RefusedError } from '../../api/client'
import { useLandscape } from '../../hooks/useLandscape'
import { useSpoke } from '../../hooks/useSpoke'
import type { LevelFilter } from '../../lib/improve'
import { scopeWords } from '../../lib/orgs'
import { AddImprovement } from './AddImprovement'
import { CorrectionDrawer } from './CorrectionDrawer'
import { Glance } from './Glance'
import { Ledger, type StatusFilter } from './Ledger'
import { RulesPanel } from './RulesPanel'
import { RunsPanel } from './RunsPanel'
import './improve.css'

// Self-improvement: the ledger of everything a person told Scout it had wrong, the nightly runs that tried to
// generalise each one, and the typed rules that came out. The screen's whole job is to make a self-improving
// system auditable — so a dead end is shown beside a win, an item the loop could not handle is tinted amber
// and says why, and a rule is rendered as the sentence it enforces rather than as the JSON it is stored in.

type Pane = 'ledger' | 'runs' | 'rules'

export function Improve() {
  const spoke = useSpoke()
  const qc = useQueryClient()
  const navigate = useNavigate()
  const landscape = useLandscape()
  const [pane, setPane] = useState<Pane>('ledger')
  const [level, setLevel] = useState<LevelFilter>('all')
  const [status, setStatus] = useState<StatusFilter>('all')
  const [kind, setKind] = useState<string>('all')
  const [open, setOpen] = useState<string | null>(null)
  const [adding, setAdding] = useState(false)

  const q = useQuery({ queryKey: ['improve', spoke], queryFn: () => improveApi.overview(spoke), staleTime: 15_000 })
  // the ledger proper: every correction with where it was addressed and what else that moved, decided server-side
  const ledger = useQuery({ queryKey: ['improve-ledger', spoke], queryFn: () => improveApi.ledger(spoke), staleTime: 15_000 })
  const runNow = useMutation({
    mutationFn: () => improveApi.runNow(spoke),
    onSuccess: (r) => navigate(`/jobs/${encodeURIComponent(r.job.id)}`),
  })
  const isAdmin = landscape.data?.viewer.isAdmin ?? false

  const pick = (next: Pane, withStatus?: StatusFilter) => {
    setPane(next)
    if (withStatus) { setStatus(withStatus); setLevel('all') }
  }
  const openCorrection = (correctionId: string) => {
    setPane('ledger')
    setOpen(correctionId)
    void qc.invalidateQueries({ queryKey: ['correction', spoke, correctionId] })
  }

  const rows = ledger.data?.rows ?? []
  const summary = ledger.data?.summary ?? { total: rows.length, atRoot: 0, bandAids: 0, open: 0, needsHuman: 0 }

  return (
    <section className="page improve">
      <header className="deals-head">
        <div>
          <p className="kicker"><Link to="/landscape">Digital Landscape</Link> · corrections{scopeWords(q.data?.scope) ? ` · ${scopeWords(q.data?.scope)}` : ''}</p>
          <h1>Self-improvement</h1>
          <p className="lede">
            Every correction on file for the org named above, and what Scout did about it. Each night the loop reads the open items,
            works out the typed rule that would have caught each one, replays that rule against the corrected item and
            every correction already on file, and applies it only if nothing flips. What it cannot generalise comes back
            to you rather than quietly disappearing.
          </p>
        </div>
        <div className="actions">
          <button className="button" onClick={() => setAdding((v) => !v)}>{adding ? 'Close the form' : 'Add an improvement'}</button>
          {isAdmin && (
            <button className="button primary" onClick={() => runNow.mutate()} disabled={runNow.isPending}
              title="Run the self-improve job now instead of waiting for the night. It reads the same open items.">
              {runNow.isPending ? 'Starting…' : 'Run now'}
            </button>
          )}
          <Link className="button" to="/landscape">Schedule</Link>
        </div>
      </header>
      {runNow.isError && <p className="error">{runNow.error instanceof RefusedError ? runNow.error.refusal.message : runNow.error.message}</p>}

      {q.isPending && <p className="muted">Loading…</p>}
      {q.isError && (
        <div className="refusal">
          <strong>{q.error instanceof RefusedError ? q.error.refusal.reason : 'Unavailable'}</strong>
          <p>{q.error instanceof RefusedError ? q.error.refusal.message : q.error.message}</p>
        </div>
      )}

      {q.isSuccess && (
        <>
          <Glance view={q.data} pane={pane} onPick={(p, s) => pick(p, s)} />

          {adding && <AddImprovement onRecorded={(id) => { setAdding(false); openCorrection(id) }} />}

          <div className="improve-body">
            <nav className="improve-rail" aria-label="Self-improvement">
              <button className={`rail-item ${pane === 'ledger' ? 'active' : ''}`} onClick={() => setPane('ledger')}>
                <span className="rail-label">Ledger</span>
                <span className="rail-count">{summary.total}</span>
                <span className="rail-blurb">what you corrected, where each was addressed, and what else it moved</span>
              </button>
              <button className={`rail-item ${pane === 'runs' ? 'active' : ''}`} onClick={() => setPane('runs')}>
                <span className="rail-label">Nightly runs</span>
                <span className="rail-count">{q.data.lastRun ? q.data.lastRun.applied : 0}</span>
                <span className="rail-blurb">every pass, what it learned and what it could not</span>
              </button>
              <button className={`rail-item ${pane === 'rules' ? 'active' : ''}`} onClick={() => setPane('rules')}>
                <span className="rail-label">Rules</span>
                <span className="rail-count">{q.data.activeRules}</span>
                <span className="rail-blurb">the typed definitions now in force</span>
              </button>
            </nav>

            <div className="improve-detail">
              {pane === 'ledger' && (
                <div className={`ledger-row ${open ? 'with-panel' : ''}`}>
                  {ledger.isPending && <p className="muted">Loading the ledger…</p>}
                  {ledger.isError && (
                    <div className="refusal">
                      <strong>Unavailable</strong>
                      <p>{ledger.error instanceof RefusedError ? ledger.error.refusal.message : ledger.error.message}</p>
                    </div>
                  )}
                  {ledger.isSuccess && (
                    <Ledger
                      rows={rows} summary={summary}
                      level={level} status={status} kind={kind}
                      onLevel={setLevel} onStatus={setStatus} onKind={setKind}
                      onOpen={(id) => setOpen(id === open ? null : id)} openId={open}
                    />
                  )}
                  {open && <CorrectionDrawer correctionId={open} onClose={() => setOpen(null)} />}
                </div>
              )}
              {pane === 'runs' && <RunsPanel onOpenCorrection={openCorrection} />}
              {pane === 'rules' && <RulesPanel onOpenCorrection={openCorrection} />}
            </div>
          </div>
        </>
      )}
    </section>
  )
}
